/* eslint-disable react/prop-types */
import React from 'react';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { Icon } from '@iconify/react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
    initializeWhatsapp,
    saveWhatsappChatMetaData,
    resetWhatsappChat,
} from 'src/redux/slices/whatsappV2';
import ChatPage from 'src/pages/whatsapp/ChatPage';

/**
 * LeadDetailWhatsappChat - Wrapper component to embed the new WhatsApp ChatPage
 * within the Lead Detail View's Communication section.
 *
 * This component handles:
 * 1. WhatsApp initialization (if not already done)
 * 2. Setting up chat metadata for the specific lead
 * 3. Rendering the ChatPage in embedded mode (no navigation, simplified header)
 */
const LeadDetailWhatsappChat = ({ leadDetail }) => {
    const dispatch = useDispatch();
    const [isInitializing, setIsInitializing] = React.useState(true);
    const [initError, setInitError] = React.useState(null);

    const {
        isLoading,
        currentBusinessId,
        currentWhatsappPhoneId,
        whatsAppChatMetaData,
    } = useSelector(
        (state) => ({
            isLoading: state?.whatsappV2?.isLoading,
            currentBusinessId: state?.whatsappV2?.currentBusinessId,
            currentWhatsappPhoneId: state?.whatsappV2?.currentWhatsappPhoneId,
            whatsAppChatMetaData: state?.whatsappV2?.whatsAppChatMetaData,
        }),
        shallowEqual
    );

    const phoneNumber = leadDetail?.lead_contact?.phone;
    const leadId = leadDetail?.id;
    const leadName = `${leadDetail?.first_name || ''} ${leadDetail?.last_name || ''}`.trim();

    // Check if WhatsApp is initialized
    const isWhatsappInitialized = !isLoading && currentBusinessId !== -1 && currentWhatsappPhoneId !== -1;

    // Initialize WhatsApp if needed
    React.useEffect(() => {
        const initWhatsapp = async () => {
            if (currentBusinessId === -1 && !isLoading) {
                setIsInitializing(true);
                setInitError(null);
                try {
                    await dispatch(initializeWhatsapp());
                } catch (error) {
                    console.error('Failed to initialize WhatsApp:', error);
                    setInitError('Failed to initialize WhatsApp. Please try again.');
                }
            }
        };

        initWhatsapp();
    }, [dispatch, currentBusinessId, isLoading]);

    // Set up chat metadata once initialized
    React.useEffect(() => {
        if (isWhatsappInitialized && leadId && phoneNumber) {
            setIsInitializing(false);

            // Only set metadata if it's for a different lead or not set
            const currentLeadId = whatsAppChatMetaData?.leadId;
            if (currentLeadId !== leadId) {
                dispatch(saveWhatsappChatMetaData({
                    leadId,
                    phoneNumber: phoneNumber?.trim().replaceAll(' ', '').replaceAll('+', ''),
                    name: leadName || phoneNumber,
                    avatar: null,
                    // Don't set thread - let fetchWhatsappChatAndOtherMetaData find it
                }));
            }
        } else if (isWhatsappInitialized && !phoneNumber) {
            setIsInitializing(false);
            setInitError('No phone number available for this lead.');
        } else if (!isLoading && currentBusinessId === -1) {
            // Initialization failed
            setIsInitializing(false);
            setInitError('WhatsApp is not configured for this practice.');
        }
    }, [isWhatsappInitialized, leadId, phoneNumber, leadName, dispatch, whatsAppChatMetaData?.leadId, isLoading, currentBusinessId]);

    // Cleanup on unmount
    React.useEffect(() => () => {
        dispatch(resetWhatsappChat());
    }, [dispatch]);

    // Handle retry
    const handleRetry = () => {
        setInitError(null);
        setIsInitializing(true);
        dispatch(initializeWhatsapp());
    };

    // Loading state
    if (isInitializing || isLoading) {
        return (
            <Box sx={styles.loadingContainer}>
                <CircularProgress size={40} sx={{ color: '#00A76F' }} />
                <Typography variant="body2" sx={{ color: '#637381', mt: 2 }}>
                    Loading WhatsApp...
                </Typography>
            </Box>
        );
    }

    // Error state
    if (initError) {
        return (
            <Box sx={styles.errorContainer}>
                <Icon icon="mdi:alert-circle-outline" width={48} height={48} style={{ color: '#FF5630' }} />
                <Typography variant="body1" sx={{ color: '#637381', mt: 2, textAlign: 'center' }}>
                    {initError}
                </Typography>
                <Button
                    variant="outlined"
                    onClick={handleRetry}
                    sx={{ mt: 2 }}
                    startIcon={<Icon icon="mdi:refresh" />}
                >
                    Retry
                </Button>
            </Box>
        );
    }

    // No phone number
    if (!phoneNumber) {
        return (
            <Box sx={styles.errorContainer}>
                <Icon icon="mdi:phone-off" width={48} height={48} style={{ color: '#919EAB' }} />
                <Typography variant="body1" sx={{ color: '#637381', mt: 2, textAlign: 'center' }}>
                    No phone number available for this lead.
                </Typography>
                <Typography variant="body2" sx={{ color: '#919EAB', mt: 1, textAlign: 'center' }}>
                    Add a phone number to start a WhatsApp conversation.
                </Typography>
            </Box>
        );
    }

    // Render ChatPage in embedded mode
    return (
        <Box sx={styles.chatContainer}>
            <ChatPage
                embedded
                onToggleDetails={() => {}}
                detailsOpen={false}
            />
        </Box>
    );
};

const styles = {
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '500px',
        backgroundColor: '#F4F6F8',
        borderRadius: '12px',
    },
    errorContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '500px',
        backgroundColor: '#F4F6F8',
        borderRadius: '12px',
        padding: '24px',
    },
    chatContainer: {
        height: '600px',
        backgroundColor: '#fff',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid rgba(145, 158, 171, 0.12)',
    },
};

export default LeadDetailWhatsappChat;
